import React, {useState, useEffect, useMemo} from 'react';
import { useHistory, useLocation } from 'react-router';
import dayjs from 'dayjs';
import moment from 'moment';
import styles from './CustomOfferDetailsPage.module.css';
import {TopBarRedesigned} from "../../../components/TopBarRedesigned";
import { formatPartialOfferTime, formatPartialOfferTimeRange, debugTimeFormatting } from '../../../utils/timeFormatting';
import {
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Event as EventIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Assignment as AssignmentIcon,
    Info as InfoIcon,
    Work as WorkIcon,
    Schedule as ScheduleIcon
} from '@mui/icons-material';
import Api from 'api'
import {BasicHygienistScoreProps, HYGIENIST_TYPES, HygienistScoreProps, OfferHygienist, PartialOffer, PositionProps, SkillsProps} from "utils/types";
import {formatProfileHygienistName, getReliabilityStatus, getReliabilityStatusObject} from "utils";
import AcceptOffer from './components/AcceptOffer';
import DeclineOffer from './components/DeclineOffer'
import BookAnotherTemp from './components/BookAnotherTemp';
import CancelShiftButton from './components/CancelShift';
import {StyledButton} from "../../../components/Buttons/DefaultButton/DefaultButton.styles";
import FixedLoader from "../../../components/FixedLoader";
import ProfileImage from './components/ProfileImage';
import RequestResubmitModal from './components/RequestResubmit';
import _ from "lodash";
import TalentResume from "../../../components/TalentResume";
import Popup from "../../../components/Popup";
import useActionApi from "../../../hooks/useActionApi";
import TimeDebugger from "../../../components/TimeDebugger/TimeDebugger";

interface OfferData {
    partialOffer: PartialOffer | null;
    hygienist: OfferHygienist | null;
    workExperience: PositionProps[];
    isFavouriteHygienist: boolean;
    job: any
}

interface LocationState {
  offerId: number;
}
declare let window: any
declare let cordova: any


interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ 
  title, 
  icon, 
  children, 
  defaultOpen = true 
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className={styles.collapsibleSection}>
      <div 
        className={styles.sectionHeader} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={styles.sectionTitle}>
          <span className={styles.sectionIcon}>{icon}</span>
          {title}
        </div>
        <div className={styles.chevronIcon}>
          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </div>
      </div>
      {isOpen && <div className={styles.sectionContent}>{children}</div>}
    </div>
  );
};

const CustomOfferDetailsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();

  const [offerId, setOfferId] = useState<number | null>(null);

  const [offerData, setOfferData] = useState<OfferData>({
    partialOffer: null,
    hygienist: null,
    workExperience: [],
    isFavouriteHygienist: false,
    job:{}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [licensed, setLicensed] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [latestWorkExperience, setLatestWorkExperience] = useState<PositionProps>();
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false)
  const [apiSkills, setApiSkills] = useState<SkillsProps[]>([])

  const [cancellationStats, setCancellationStats] = useState<BasicHygienistScoreProps>({
    cancellationScore: 0,
    overallReliability: 0,
    recentReliability: 0,
    weightedReliability: 0
});

  const goBack = () => {
    history.goBack();
  }

  const [, , getSkills] = useActionApi({
    apiMethod: Api.getSkills,
    onSuccess: (skills: SkillsProps[]) => {
      if(_.isEmpty(skills)) return
      setApiSkills(skills)
    },
  });

  const [statsLoading, , getStats] = useActionApi({
    apiMethod: Api.getCancellationStats,
    onSuccess: (data: BasicHygienistScoreProps) => {
      setCancellationStats(data)
    },
  })

  const fetchData = async (id: number) => {
    setLoading(true);
    setError(null);

    try {
      const [partialOffer, partialOfferViewsResponse] = await Promise.all([
        Api.getPartialOffer({ offerId: id }),
        Api.updatePartialOfferViews({ offerId: id })
      ]);

      if (!partialOffer || !partialOffer.hygienistId) {
        throw new Error("Invalid offer data");
      }
      
      // Use the job data that comes with the partial offer (which has originalPostedStart/End)
      // instead of making a separate API call that would overwrite it
      
      let [hygienistResume] = await Promise.all([
        Api.getResumeData({hygienistId: partialOffer.hygienistId, locationId: partialOffer.location.id}),
        getSkills({type: partialOffer.job?.type, state: partialOffer.location.province}),
        getStats({hygienistId: partialOffer.hygienistId})
      ]);

      if (hygienistResume?.type === HYGIENIST_TYPES.HYGIENIST && hygienistResume?.CDHONumber) {
        setLicensed(true);
      }

      setOfferData({
        partialOffer,
        hygienist: hygienistResume,
        workExperience: hygienistResume?.workExperience,
        isFavouriteHygienist: hygienistResume?.isFavouriteHygienist,
        job: partialOffer.job // Use job data from partial offer response
      })
      
    } catch (err) {
      console.error("Error fetching offer data:", err);
      setError("Failed to fetch offer details");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const currentOfferId = location.state?.offerId;
    if (currentOfferId && currentOfferId !== offerId) {
      setOfferId(currentOfferId);
      fetchData(currentOfferId);
    }
  }, [location.state, offerId]);

  useEffect(() => {
    if(!_.isEmpty(offerData)) {
      const now = new Date();
      const expiresAt = new Date(offerData.partialOffer?.expiresAt || '');
      const timeDiff = expiresAt.getTime() - now.getTime();
      setIsExpired(timeDiff <= 0);
    }
  }, [offerData]);

  const openResume = () => {
    if (showResume) {
      window.Cordova ?
        cordova?.InAppBrowser.open(
          offerData.hygienist?.resumeUrl,
          '_system',
          'location=yes'
        ) : window.open(offerData.hygienist?.resumeUrl);
    } else {
      setIsResumeModalOpen(true)
      return
    }
  }

  const showResume = offerData.hygienist?.resumeUrl && offerData.hygienist?.skills.length === 0;

  const renderRequestResubmitModal = () => {
    if (!offerData.job || offerId === null) {
      return null;
    }

    return (
      <RequestResubmitModal
        job={offerData.job}
        history={history}
        expiredOfferId={offerId!}      />
    );
  };

  useEffect(() => {
    if (offerData?.workExperience) {
      let latestWorkExperience = offerData.workExperience.find((experience) => experience.current );
      if (!latestWorkExperience) {
        latestWorkExperience = offerData.workExperience.sort((a, b) => {
          return dayjs(b.toDate).valueOf() - dayjs(a.toDate).valueOf();
        })[0]
      }
      setLatestWorkExperience(latestWorkExperience);
    }
  }, [offerData]);

  const professionalLicense = _.isEmpty(offerData.hygienist)
    ? '' : formatProfileHygienistName(offerData.hygienist, offerData.hygienist?.skills);

  const statusObject = useMemo(() => getReliabilityStatusObject(cancellationStats.overallReliability || 0), [cancellationStats.overallReliability])

  const { formattedStartTime: formattedPartialStartTime, formattedEndTime: formattedPartialEndTime } = useMemo(() => {
    if ((process.env.NODE_ENV as string) === 'staging' || (process.env.NODE_ENV as string) === 'local') {
      debugTimeFormatting(
        offerData.partialOffer?.offeredStartTime,
        offerData.partialOffer?.offeredStartTimeLocal,
        'CustomOfferDetailsPage - Partial Times'
      );
    }

    return formatPartialOfferTimeRange(
      offerData.partialOffer?.offeredStartTime,
      offerData.partialOffer?.offeredEndTime,
      offerData.partialOffer?.offeredStartTimeLocal,
      offerData.partialOffer?.offeredEndTimeLocal,
      offerData.partialOffer?.location?.lat,
      offerData.partialOffer?.location?.lon
    );
  }, [offerData.partialOffer]);

  const isPartialHours = useMemo(() => {
    if (!offerData.partialOffer || !offerData.job?.shifts?.[0]) return false;
    
    const jobStartUTC = offerData.job.shifts[0].originalPostedStart;
    const jobEndUTC = offerData.job.shifts[0].originalPostedEnd;
    const offerStartUTC = offerData.partialOffer.offeredStartTime;
    const offerEndUTC = offerData.partialOffer.offeredEndTime;
    
    if (!jobStartUTC || !jobEndUTC) {
      const jobStartFormatted = formatPartialOfferTime(offerData.job.shifts[0].postedStart, undefined, offerData.partialOffer?.location?.lat, offerData.partialOffer?.location?.lon);
      const jobEndFormatted = formatPartialOfferTime(offerData.job.shifts[0].postedEnd, undefined, offerData.partialOffer?.location?.lat, offerData.partialOffer?.location?.lon);
      const offerStartFormatted = formatPartialOfferTime(offerStartUTC, undefined, offerData.partialOffer?.location?.lat, offerData.partialOffer?.location?.lon);
      const offerEndFormatted = formatPartialOfferTime(offerEndUTC, undefined, offerData.partialOffer?.location?.lat, offerData.partialOffer?.location?.lon);
      
      return jobStartFormatted !== offerStartFormatted || jobEndFormatted !== offerEndFormatted;
    }
    
    const isPartial = jobStartUTC !== offerStartUTC || jobEndUTC !== offerEndUTC;
    
    return isPartial;
  }, [offerData.partialOffer, offerData.job]);

  const formatExpiryTime = () => {
    if (!offerData.partialOffer?.expiresAt) return '';
    
    const now = new Date();
    const expiresAt = new Date(offerData.partialOffer.expiresAt);
    const diffMs = expiresAt.getTime() - now.getTime();
    
    if (diffMs <= 0) return 'Expired';
    
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `Offer Expires in ${diffHrs}h ${diffMins}m`;
  };

  const renderRatings = () => {
    const rating = offerData.hygienist?.starScore || 4;
    const ratingCount = offerData.hygienist?.numRatedJobs || 44;
    
    return (
      <div className={styles.ratingContainer}>
        <div className={styles.stars}>
          {[1, 2, 3, 4, 5].map((star) => (
            star <= Math.floor(rating) ? 
              <StarIcon key={star} className={styles.star} /> : 
              <StarBorderIcon key={star} className={styles.emptyStar} />
          ))}
        </div>
        <span className={styles.ratingsCount}>
          {ratingCount} {ratingCount === 1 ? 'Rating' : 'Ratings'}
        </span>
      </div>
    );
  };

  return (
    <>
    {loading ? <FixedLoader loading={loading || statsLoading} /> :
      <div className={styles.container}>
        <TopBarRedesigned
          title={'Custom Offer Details'}
          onBack={goBack}
        />

        <Popup
          opened={isResumeModalOpen}
          onClose={() => setIsResumeModalOpen(false)}
        >
          <TalentResume
            hygienist={offerData?.hygienist}
            onClose={() => setIsResumeModalOpen(false)}
          />
        </Popup>

        <div className={styles.shiftDetailsCard}>
          <div className={styles.shiftHeadingRow}>
            <h3 className={styles.shiftHeading}>Dental Hygienist Shift</h3>
            <div className={styles.shiftDate}>
              <EventIcon className={styles.shiftDateIcon} />
              {dayjs(offerData.partialOffer?.job?.startDate).format('dddd, MMMM D')}
            </div>
          </div>
          
          <div className={styles.shiftCard}>
            <div className={styles.locationColumn}>
              <div className={styles.shiftLocation}>
                {offerData.partialOffer?.location?.address},
                <br />
                {offerData.partialOffer?.location?.city}, {offerData.partialOffer?.location?.province}
              </div>
            </div>
            
            <div className={styles.timesColumn}>
              <div className={styles.shiftTimeGroup}>
                <span className={styles.shiftTimeLabel}>Start:</span>
                <span className={styles.shiftTimeValue}>
                  {formattedPartialStartTime}
                </span>
              </div>
              
              <div className={styles.shiftTimeGroup}>
                <span className={styles.shiftTimeLabel}>End:</span>
                <span className={styles.shiftTimeValue}>
                  {formattedPartialEndTime}
                </span>
              </div>
            </div>
          </div>

          {/* Partial Hours Offer Section */}
          {isPartialHours && (
            <div className={styles.partialHoursContainer}>
              <div className={styles.partialHoursHeader}>
                <div className={styles.dashedCircle}></div>
                <span className={styles.partialHoursTitle}>Partial Hours Offer</span>
              </div>
              
              <div className={styles.hoursContent}>
                <div className={styles.hoursSection}>
                  <div className={styles.hoursLabel}>Hours Offered:</div>
                  <div className={styles.hoursValue}>
                    {formattedPartialStartTime} - {formattedPartialEndTime}
                  </div>
                </div>
                
                <div className={styles.hoursSection}>
                  <div className={styles.hoursLabel}>Original Shift Hours:</div>
                  <div className={styles.originalHoursValue}>
                    {formatPartialOfferTime(offerData.job?.shifts?.[0]?.postedStart, undefined, offerData.partialOffer?.location?.lat, offerData.partialOffer?.location?.lon)} - {formatPartialOfferTime(offerData.job?.shifts?.[0]?.postedEnd, undefined, offerData.partialOffer?.location?.lat, offerData.partialOffer?.location?.lon)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={styles.hygienistOfferCard}>
          <div className={styles.profileHeader}>
            <div className={styles.profileNameAndTitle}>
              {offerData.hygienist?.photoUrl && (
                <ProfileImage
                  photoUrl={offerData.hygienist.photoUrl}
                  className={styles.profileImage}
                />
              )}
              <div className={styles.profileInfo}>
                <h2 className={styles.profileName}>{professionalLicense}</h2>
                <p className={styles.graduatedText}>
                  graduated {offerData.hygienist?.graduationYear} from {offerData.hygienist?.school}
                </p>
                {offerData.hygienist?.type === HYGIENIST_TYPES.HYGIENIST && (
                  <p className={styles.leftHanded}>
                    {offerData.hygienist?.firstName} is {offerData.hygienist?.isLeftHanded ? 'Left' : 'Right'} handed
                  </p>
                )}
              </div>
            </div>
            <div className={styles.hourlyRate}>
              <p className={styles.hourlyRateLabel}>Hourly rate</p>
              <p className={styles.rate}>${offerData.partialOffer?.hourlyRate}/hr</p>
            </div>
          </div>
          {renderRatings()}
          
          <p className={styles.offerExpiry}>{formatExpiryTime()}</p>

          <div className={styles.buttonsContainer}>
            {!isExpired ? (
              <>
                <AcceptOffer
                  offerData={{
                    isPartialHours: isPartialHours,
                    partialOffer: offerData.partialOffer!,
                    hygienist: offerData.hygienist!,
                    skills: offerData.hygienist?.skills || [],
                  }}
                  offerId={offerId!}
                  originalShiftHours={{
                    startTime: formatPartialOfferTime(offerData.job?.shifts?.[0]?.postedStart, undefined, offerData.partialOffer?.location?.lat, offerData.partialOffer?.location?.lon),
                    endTime: formatPartialOfferTime(offerData.job?.shifts?.[0]?.postedEnd, undefined, offerData.partialOffer?.location?.lat, offerData.partialOffer?.location?.lon)
                  }}
                  partialShiftHours={{
                    startTime: formattedPartialStartTime,
                    endTime: formattedPartialEndTime,
                  }}
                />
                <DeclineOffer
                  partialOffer={offerData.partialOffer!}
                  offerId={offerId as number}
                  key={offerId}
                />
                <StyledButton
                  variant="outlined"
                  color="primary"
                  size="small"
                  id="continue-button"
                  className={styles.resumeButton}
                  onClick={openResume}
                >
                  View Resume
                </StyledButton>
              </>
            ) : renderRequestResubmitModal()}
          </div>
        </div>

        {/* Skills & Qualifications Section */}
        {offerData.hygienist?.CDHONumber && (
          <CollapsibleSection 
            title="Skills & Qualification (self-reported)" 
            icon={<AssignmentIcon />}
          >
            <p className={styles.licenseInfo}>License #: {offerData.hygienist.CDHONumber}</p>
            <p style={{ fontWeight: '500', marginBottom: '0.75rem' }}>Training & Certifications received:</p>
            {offerData.hygienist.skills?.length > 0 && offerData.hygienist.skills.map((skill, index) => (
              <div key={index} className={styles.skillItem}>
                <CheckCircleIcon className={styles.checkIcon} />
                <span>{skill.label}</span>
              </div>
            ))}
          </CollapsibleSection>
        )}

        {/* Work Experience Section */}
        {offerData.workExperience?.length > 0 && (
          <CollapsibleSection 
            title="Work Experience" 
            icon={<WorkIcon />}
          >
            {offerData.workExperience.map((experience, index) => {
              const jobTitle = 'jobTitle' in experience ? experience.jobTitle : '';
              const companyName = 'companyName' in experience ? experience.companyName : '';
              
              return (
                <div key={index} className={styles.workExperienceItem}>
                  <h4 className={styles.jobTitle}>
                    {jobTitle}
                  </h4>
                  <p className={styles.companyName}>
                    {companyName}
                  </p>
                  <p className={styles.jobPeriod}>
                    {experience.fromDate ? dayjs(experience.fromDate).format('MMMM YYYY') : 'January 2021'} - {
                      experience.current ? 
                        'Present' : 
                        (experience.toDate ? dayjs(experience.toDate).format('MMMM YYYY') : '')
                    }
                  </p>
                </div>
              );
            })}
          </CollapsibleSection>
        )}

        {/* About Me Section */}
        {offerData.hygienist?.about && (
          <CollapsibleSection 
            title="About Me" 
            icon={<InfoIcon />}
          >
            <p className={styles.aboutText}>{offerData.hygienist.about}</p>
          </CollapsibleSection>
        )}

        {/* Reminder Section */}
        <div className={styles.reminderSection}>
          <div className={styles.reminderHeader}>
            <WarningIcon className={styles.warningIcon} /> 
            <span className={styles.reminderTitle}>friendly reminder</span>
          </div>
          <p className={styles.reminderText}>
            Standard placement fees apply to shifts booked with {offerData.hygienist?.firstName} - 
            whether arranged on the app or through direct contact
          </p>
        </div>

        {/* Bottom Buttons */}
        <div className={styles.bottomButtons}>
          {offerData.partialOffer?.job && (
            <>
              <BookAnotherTemp job={offerData.job} />
              <CancelShiftButton job={offerData.job} />
            </>
          )}
        </div>

        <TimeDebugger
          timeData={{
            offeredStartTime: offerData.partialOffer?.offeredStartTime,
            offeredEndTime: offerData.partialOffer?.offeredEndTime,
            offeredStartTimeLocal: offerData.partialOffer?.offeredStartTimeLocal,
            offeredEndTimeLocal: offerData.partialOffer?.offeredEndTimeLocal,
            postedStartTime: offerData.job?.shifts?.[0]?.postedStart,
            postedEndTime: offerData.job?.shifts?.[0]?.postedEnd,
          }}
          location={{
            lat: offerData.partialOffer?.location?.lat,
            lon: offerData.partialOffer?.location?.lon,
          }}
        />
      </div>
    }
    </>
  );
};

export default CustomOfferDetailsPage;